import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../api/apiClient';
import { useApiCall } from './useApi';

export function usePlatformSummary() {
  const { data, loading, error } = useApiCall('/platform/summary');
  return { data, loading, error };
}

export function usePlatformMsps() {
  const api = useApiClient();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);

  const load = useCallback(({ force = false } = {}) => {
    let cancelled = false;
    const cacheKey = 'platform-msps';
    let hasCache = false;

    if (!force) {
      try {
        const cached = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(cacheKey) : null;
        if (cached) {
          const { data: cachedData, ts } = JSON.parse(cached);
          const rows = Array.isArray(cachedData) ? cachedData : [];
          hasCache = rows.length > 0;
          if (hasCache) {
            setData(rows);
            setLoading(false);
            setRefreshing(true);
            setIsStale(Date.now() - Number(ts ?? 0) > 120000);
          }
        }
      } catch (_) {}
    }

    if (!hasCache) setLoading(true);
    setError(null);

    api.get('/platform/msps')
      .then(result => {
        if (cancelled) return;
        const rows = Array.isArray(result) ? result : Array.isArray(result?.items) ? result.items : Array.isArray(result?.msps) ? result.msps : [];
        setData(rows);
        setIsStale(false);
        try {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(cacheKey, JSON.stringify({ data: rows, ts: Date.now() }));
          }
        } catch (_) {}
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => { cancelled = true; };
  }, [api]);

  useEffect(() => load(), [load]);

  const refetch = useCallback(() => {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('platform-msps');
      }
    } catch (_) {}
    load({ force: true });
  }, [load]);

  return { data, loading, refreshing, isStale, error: data.length > 0 ? null : error, refetch };
}

export function usePlatformMsp(mspId) {
  const { data, loading, error } = useApiCall(mspId ? `/platform/msps/${mspId}` : null);
  return { data, loading, error };
}
